import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AvatarProps {
  /** Image source URL */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Name used for initials fallback */
  name?: string;
  /** Size in pixels @default 48 */
  size?: number;
  /** Maximum number of initials to show (1–4) @default 1 */
  maxInitials?: 1 | 2 | 3 | 4;
  /** Custom fallback content (overrides initials) */
  fallback?: React.ReactNode;
  /** Secondary image URL tried if `src` fails */
  fallbackSrc?: string;
  /** Background color when showing the fallback @default '#e0e0e0' */
  fallbackBgColor?: string;
  /** Initials text color @default '#757575' */
  fallbackTextColor?: string;
  /** Border radius @default '50%' */
  borderRadius?: string | number;
  /** Click handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Additional MUI sx styles for the container */
  sx?: SxProps<Theme>;
  /** Additional class name */
  className?: string;
}

// ── Styled components ─────────────────────────────────────────────────────────

const AvatarContainer = styled(Box, {
  shouldForwardProp: (p) => !['size', 'radius', 'bgColor', 'clickable'].includes(p as string),
})<{ size: number; radius: string | number; bgColor: string; clickable: boolean }>(
  ({ size, radius, bgColor, clickable }) => ({
    alignItems: 'center',
    backgroundColor: bgColor,
    borderRadius: radius,
    cursor: clickable ? 'pointer' : 'default',
    display: 'flex',
    flexShrink: 0,
    height: size,
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    width: size,
  }),
);

const FallbackText = styled(Typography, {
  shouldForwardProp: (p) => p !== 'textColor',
})<{ textColor: string }>(({ textColor }) => ({
  color: textColor,
  fontWeight: 500,
  userSelect: 'none',
}));

const ImageWrapper = styled(Box)({
  height: '100%',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function getVariant(size: number): 'h4' | 'h5' | 'h6' | 'body1' | 'body2' {
  if (size >= 100) return 'h4';
  if (size >= 80) return 'h5';
  if (size >= 56) return 'h6';
  if (size >= 40) return 'body1';
  return 'body2';
}

function getInitials(name: string, maxChars: number = 1): string {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  const wordCount = words.length;
  const chars = Math.min(Math.max(maxChars, 1), 4);

  if (wordCount === 1) {
    const firstWord = words[0];
    return chars === 1
      ? firstWord.charAt(0).toUpperCase()
      : firstWord.charAt(0).toUpperCase() + firstWord.substring(1, chars).toLowerCase();
  }

  if (wordCount === 2) {
    const [f, l] = words;
    if (chars === 1) return f.charAt(0).toUpperCase();
    if (chars === 2) return f.charAt(0).toUpperCase() + l.charAt(0).toUpperCase();
    if (chars === 3)
      return (
        f.charAt(0).toUpperCase() + (f.charAt(1) || '').toLowerCase() + l.charAt(0).toUpperCase()
      );
    return (
      f.charAt(0).toUpperCase() +
      (f.charAt(1) || '').toLowerCase() +
      l.charAt(0).toUpperCase() +
      (l.charAt(1) || '').toLowerCase()
    );
  }

  // 3+ words
  const first = words[0];
  const last = words[wordCount - 1];
  const middles = words.slice(1, -1);
  if (chars === 1) return first.charAt(0).toUpperCase();
  if (chars === 2) return first.charAt(0).toUpperCase() + last.charAt(0).toUpperCase();
  if (chars === 3)
    return (
      first.charAt(0).toUpperCase() +
      middles[0].charAt(0).toUpperCase() +
      last.charAt(0).toUpperCase()
    );
  if (wordCount === 3)
    return (
      first.charAt(0).toUpperCase() +
      (first.charAt(1) || '').toLowerCase() +
      middles[0].charAt(0).toUpperCase() +
      last.charAt(0).toUpperCase()
    );
  return words
    .slice(0, 4)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Avatar displays a user's profile image with a graceful fallback to initials.
 *
 * @example
 * // With image
 * <Avatar src="/user.jpg" name="John Doe" size={64} />
 *
 * @example
 * // Initials only
 * <Avatar name="Jane Smith" size={40} />
 *
 * @example
 * // Custom fallback content
 * <Avatar fallback={<PersonIcon />} size={48} />
 */
export const Avatar: React.FC<AvatarProps> = ({
  alt,
  borderRadius = '50%',
  className,
  fallback,
  fallbackBgColor = '#e0e0e0',
  fallbackSrc,
  fallbackTextColor = '#757575',
  maxInitials = 1,
  name,
  onClick,
  size = 48,
  src,
  sx,
}) => {
  const altText = alt ?? name ?? 'Avatar';

  const renderFallback = () => {
    if (fallback) return fallback;
    return (
      <FallbackText variant={getVariant(size)} textColor={fallbackTextColor}>
        {getInitials(name ?? '', maxInitials)}
      </FallbackText>
    );
  };

  return (
    <AvatarContainer
      size={size}
      radius={borderRadius}
      bgColor={fallbackBgColor}
      clickable={!!onClick}
      onClick={onClick}
      sx={sx}
      className={className}
    >
      {/* Fallback is always rendered as the base layer */}
      {renderFallback()}

      {/* Image overlays the fallback when src is provided */}
      {src && (
        <ImageWrapper>
          <ResponsiveImage
            src={src}
            alt={altText}
            fallbackSrc={fallbackSrc}
            sx={{ height: '100%', objectFit: 'cover', width: '100%' }}
          />
        </ImageWrapper>
      )}
    </AvatarContainer>
  );
};

export default Avatar;
